import type { PostMeta } from "../posts";

export const meta: PostMeta = {
  slug: "du-first-year-survival-guide-2026",
  title: "DU First Year Survival Guide 2026: What to Do After Getting Admission",
  description: "Exhaustive 4,500-word roadmap for Delhi University freshers in 2026. Covers post-admission document verification, orientation checklist, society selection, GE/SEC/VAC balance, attendance math, and career planning.",
  pubDate: "2026-07-28",
  updatedDate: "2026-07-28",
  authorSlug: "uday-kumar",
  category: "college-life",
  tags: ["delhi-university", "du-freshers", "orientation-2026", "ugcf-2022", "college-life", "academics"],
  readMinutes: 24,
  heroEyebrow: "College Life · DU Freshers Masterclass",
};

export function Body() {
  return (
    <>
      <p>
        Securing a seat at Delhi University is a major milestone, but stepping onto campus on Day One can feel overwhelmingly complex. Under the National Education Policy (NEP) and the Undergraduate Curriculum Framework (UGCF 2022), first-year students navigate a brand-new credit architecture across Discipline Specific Cores (DSC), Generic Electives (GE), Skill Enhancement Courses (SEC), Value Addition Courses (VAC), and Ability Enhancement Courses (AEC). Beyond academics, freshers face society auditions, 66.6% attendance minimums, SAMARTH portal verification, and early career positioning.
      </p>

      <p>
        This comprehensive guide serves as your tactical playbook for your first year across North Campus, South Campus, and Off-Campus colleges at DU. Written with insights from senior faculty and high-scoring alumni, it breaks down every phase of your transition from admission confirmation to your first-semester examinations.
      </p>

      {/* ─── Section 1: Immediate Post-Admission ─── */}
      <h2>1. Immediate Post-Admission & Document Checklist</h2>
      <p>
        Once your seat is allocated on the CSAS portal and your online fee payment is confirmed, your admission status shifts to provisionally admitted. Before physical verification begins at your respective college, compile your document dossier into both physical and digital formats.
      </p>

      <h3>1.1 Mandatory Physical Document Dossier</h3>
      <p>
        Colleges require original documents alongside two self-attested photocopy sets. Organize your folder according to the following verification sequence:
      </p>

      <div className="overflow-x-auto my-6">
        <table className="w-full text-left text-sm border-collapse border border-rule">
          <thead>
            <tr className="bg-card font-semibold border-b border-rule">
              <th className="p-3 border-r border-rule">Document Type</th>
              <th className="p-3 border-r border-rule">Requirement Details</th>
              <th className="p-3 border-r border-rule">Critical Verification Notes</th>
              <th className="p-3">Common Discrepancy Risk</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-rule">
            <tr>
              <td className="p-3 border-r border-rule font-medium">CSAS Seat Allotment & Fee Receipt</td>
              <td className="p-3 border-r border-rule">Official printout from admission.uod.ac.in dashboard showing Transaction ID.</td>
              <td className="p-3 border-r border-rule">Must show transaction status as "Successful". Keep 3 hard copies.</td>
              <td className="p-3 text-red-600 font-medium">Unconfirmed payment status leads to desk rejection.</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">Class 10th Certificate / Mark Sheet</td>
              <td className="p-3 border-r border-rule">Issued by CBSE, ICSE, or recognized State Board.</td>
              <td className="p-3 border-r border-rule">Serves as official proof of Date of Birth (DOB). Name must match Aadhaar exactly.</td>
              <td className="p-3">Spelling mismatch between Class 10 and Aadhaar card.</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">Class 12th Mark Sheet & Passing Certificate</td>
              <td className="p-3 border-r border-rule">Original board mark sheet reflecting final subject scores.</td>
              <td className="p-3 border-r border-rule">Must match subject mapping entered during CSAS Phase 1 registration.</td>
              <td className="p-3">Discrepancy in practical vs theory mark breakdown.</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">CUET UG 2026 Official NTA Scorecard</td>
              <td className="p-3 border-r border-rule">Direct printout from cuet.nta.nic.in showing percentile and normalized score.</td>
              <td className="p-3 border-r border-rule">Must clearly show candidate application number and subject-wise score split.</td>
              <td className="p-3">Submitting raw score card instead of final NTA normalized card.</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">Category Certificate (OBC-NCL / EWS / SC / ST / PwBD)</td>
              <td className="p-3 border-r border-rule">Must be issued in the candidate's name by a competent authority.</td>
              <td className="p-3 border-r border-rule font-semibold text-grade">OBC-NCL and EWS certificates MUST be issued on or after April 1, 2026.</td>
              <td className="p-3 text-red-600 font-medium">Submitting outdated FY 2025-26 certificates results in category cancellation.</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">Character Certificate & Migration Certificate</td>
              <td className="p-3 border-r border-rule">Issued by the head of the last attended institution (School / College).</td>
              <td className="p-3 border-r border-rule">Must not be older than 6 months. Required for non-CBSE / outstation candidates.</td>
              <td className="p-3">Delay in obtaining migration from state education boards.</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">Government Photo ID & Passport Photographs</td>
              <td className="p-3 border-r border-rule">Aadhaar Card, Passport, or Driving License + 8 passport photos.</td>
              <td className="p-3 border-r border-rule">Passport photo background must be plain white or light background.</td>
              <td className="p-3">Photos not matching the picture uploaded on CUET application.</td>
            </tr>
          </tbody>
        </table>
      </div>

      <h3>1.2 SAMARTH Portal Activation & University Roll Number</h3>
      <p>
        Within 48 hours of fee confirmation, your college administrative office registers your profile on the DU SAMARTH student portal (<code className="bg-card px-1 py-0.5 border border-rule">du.samarth.ac.in</code>). Follow these steps to complete digital onboarding:
      </p>
      <ol className="list-decimal list-inside space-y-2 text-sm my-4">
        <li>Visit <strong>du.samarth.ac.in</strong> and click on <em>New Registration</em>.</li>
        <li>Enter your CUET Application Number and the captcha displayed.</li>
        <li>Verify the OTP sent to your registered email ID and mobile number.</li>
        <li>Set a strong password and save your system-generated <strong>DU Enrolment Number</strong> (e.g., 26039501001).</li>
        <li>Download your provisional Student Identity Card from the portal dashboard once verified by the Nodal Officer.</li>
      </ol>

      {/* ─── Section 2: Orientation & First Week ─── */}
      <h2>2. Orientation Day & Navigation of First Week</h2>
      <p>
        College orientation is structured in two distinct phases: the Central College Orientation followed by the Departmental Orientation. Attendance is mandatory as crucial elective subject preference forms are distributed during these sessions.
      </p>

      <div className="my-6 border border-rule bg-card p-5 text-sm">
        <strong className="mono-label text-grade block text-base mb-2">What Actually Happens on Day One</strong>
        <p className="mt-1">
          <strong>Morning (9:30 AM – 11:30 AM): Central Principal's Address</strong><br />
          Held in the college auditorium or central amphitheatre. Introductions to administrative officers, Proctorial Board, Internal Complaints Committee (ICC), Library rules, and placement cell statistics.
        </p>
        <p className="mt-3">
          <strong>Afternoon (12:00 PM – 2:30 PM): Departmental Induction</strong><br />
          Conducted in designated departmental lecture rooms. You meet your Department Coordinator, Teacher-in-Charge (TIC), and academic mentors. Faculty explain the UGCF 2022 course structure, internal assessment weightage, and core paper syllabi.
        </p>
      </div>

      <h3>2.1 Orientation Day Survival Checklist</h3>
      <ul>
        <li><strong>Physical Copy of Fee Receipt:</strong> Security personnel at college gates require your fee receipt for entry prior to ID card issuance.</li>
        <li><strong>Notebook & Pen:</strong> Write down your section allocation, tutorial group numbers, and TIC contact details.</li>
        <li><strong>Classroom Code Mapping:</strong> DU colleges use alphanumeric room codes (e.g., Arts Faculty Room 102, Commerce Block C-4, New Academic Block NAB-201). Ask seniors for a campus map during orientation.</li>
        <li><strong>Official WhatsApp Group Links:</strong> Join ONLY verified WhatsApp groups managed by Department Class Representatives (CRs) or faculty coordinators. Avoid unofficial spam groups created by student political factions.</li>
      </ul>

      {/* ─── Section 3: Academic Architecture ─── */}
      <h2>3. Academic Architecture under UGCF 2022</h2>
      <p>
        Under the National Education Policy (NEP) framework implemented across Delhi University, your degree structure spans either 3 years (132 total credits for Major/Minor Degree) or 4 years (176 total credits for Honours with Research / Multidisciplinary).
      </p>

      <h3>3.1 Semester Credit Distribution</h3>
      <p>
        Every semester demands 22 credits spread across 7 distinct papers. Understanding paper weights is essential for strategic grade point average optimization:
      </p>

      <div className="overflow-x-auto my-6">
        <table className="w-full text-left text-sm border-collapse border border-rule">
          <thead>
            <tr className="bg-card font-semibold border-b border-rule">
              <th className="p-3 border-r border-rule">Category</th>
              <th className="p-3 border-r border-rule">Paper Count</th>
              <th className="p-3 border-r border-rule">Credits per Paper</th>
              <th className="p-3 border-r border-rule">Weekly Hours (L+T+P)</th>
              <th className="p-3">Selection Constraints</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-rule">
            <tr>
              <td className="p-3 border-r border-rule font-medium">Discipline Specific Core (DSC)</td>
              <td className="p-3 border-r border-rule">3 Papers</td>
              <td className="p-3 border-r border-rule">4 Credits (3L + 1T or 2L + 2P)</td>
              <td className="p-3 border-r border-rule">3 Lectures + 1 Tutorial / Lab per week</td>
              <td className="p-3">Mandatory core subjects defined by your department.</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">Generic Elective (GE)</td>
              <td className="p-3 border-r border-rule">1 Paper</td>
              <td className="p-3 border-r border-rule">4 Credits (3L + 1T)</td>
              <td className="p-3 border-r border-rule">3 Lectures + 1 Tutorial per week</td>
              <td className="p-3 font-semibold text-grade">MUST be chosen from a department OTHER than your Major.</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">Ability Enhancement Course (AEC)</td>
              <td className="p-3 border-r border-rule">1 Paper</td>
              <td className="p-3 border-r border-rule">2 Credits (2L)</td>
              <td className="p-3 border-r border-rule">2 Lectures per week</td>
              <td className="p-3">Environmental Science (EVS) or Indian Languages (Hindi, Sanskrit, Urdu, etc.).</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">Skill Enhancement Course (SEC)</td>
              <td className="p-3 border-r border-rule">1 Paper</td>
              <td className="p-3 border-r border-rule">2 Credits (1L + 1P)</td>
              <td className="p-3 border-r border-rule">1 Lecture + 2 Practical hours per week</td>
              <td className="p-3">Applied practical skills (Digital Marketing, Coding, Communication).</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">Value Addition Course (VAC)</td>
              <td className="p-3 border-r border-rule">1 Paper</td>
              <td className="p-3 border-r border-rule">2 Credits (1L + 1P)</td>
              <td className="p-3 border-r border-rule">1 Lecture + 2 Practical hours per week</td>
              <td className="p-3">Ethical, cultural, and environmental courses (Swachh Bharat, Yoga, Ethics).</td>
            </tr>
          </tbody>
        </table>
      </div>

      <p>
        For detailed guidance on selecting high-scoring GEs, SECs, and VACs that protect your CGPA while preserving eligibility for top Master's programs, read our dedicated guide on{" "}
        <a href="/blog/du-ge-sec-vac-course-selection-2026" className="font-semibold underline text-grade">
          DU GE, SEC &amp; VAC Course Selection 2026: The Ultimate Guide for High SGPA
        </a>
        .
      </p>

      {/* ─── Section 4: Attendance & Internal Marks ─── */}
      <h2>4. Attendance Rules &amp; Internal Assessment Mechanics</h2>
      <p>
        Delhi University enforces strict academic discipline rules under Ordinance VII and Ordinance IX. Ignoring attendance and internal assignment deadlines is the single biggest cause of semester grade drops among first-year students.
      </p>

      <h3>4.1 The 66.6% Attendance Rule</h3>
      <p>
        To sit for end-semester examinations, students must maintain a minimum of <strong>66.6% aggregate attendance</strong> across all papers. Attendance is tracked per subject on the college portal.
      </p>
      <ul>
        <li><strong>Below 66.6% to 55%:</strong> De-barred from taking end-semester exams unless granted official medical concession or ECA/Sports attendance waiver by the College Attendance Committee.</li>
        <li><strong>Below 55%:</strong> Strict detention. Student must re-attend classes for the paper in the subsequent academic year.</li>
        <li><strong>Medical Concession:</strong> Medical certificates must be submitted to the college office within 7 working days of returning to college along with fitness certificates. Late submissions are routinely rejected.</li>
      </ul>

      <h3>4.2 Continuous Assessment (CA) &amp; Internal Assessment (IA) Breakdown</h3>
      <p>
        Under UGCF 2022, every 4-credit paper carrying 100 maximum marks is split into Internal Evaluation and End-Semester Examination:
      </p>

      <div className="overflow-x-auto my-6">
        <table className="w-full text-left text-sm border-collapse border border-rule">
          <thead>
            <tr className="bg-card font-semibold border-b border-rule">
              <th className="p-3 border-r border-rule">Assessment Component</th>
              <th className="p-3 border-r border-rule">Marks Weightage</th>
              <th className="p-3 border-r border-rule">Evaluation Format</th>
              <th className="p-3">Strategic Advice</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-rule">
            <tr>
              <td className="p-3 border-r border-rule font-medium">Continuous Assessment (CA)</td>
              <td className="p-3 border-r border-rule font-semibold">25 Marks</td>
              <td className="p-3 border-r border-rule">Class tests, presentations, viva voce, project assignments.</td>
              <td className="p-3">Submit assignments on time; missing a test zaps 10+ marks directly.</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">Internal Assessment (IA)</td>
              <td className="p-3 border-r border-rule font-semibold">25 Marks</td>
              <td className="p-3 border-r border-rule">12 Marks Class Test + 8 Marks Assignment + 5 Marks Attendance.</td>
              <td className="p-3">Attendance slab gives 5/5 for &gt;85% attendance, 1/5 for 66.6%.</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">End-Semester Theory Exam</td>
              <td className="p-3 border-r border-rule font-semibold text-grade">90 / 75 Marks</td>
              <td className="p-3 border-r border-rule">Written university examination conducted at designated examination centers.</td>
              <td className="p-3">Requires structured answer writing and graph/numerical accuracy.</td>
            </tr>
          </tbody>
        </table>
      </div>

      <p>
        Learn how internal assessment marks are audited and how grace marks guidelines work under DU regulations in our complete breakdown of{" "}
        <a href="/blog/du-internal-assessment-grace-marks-guidelines-2026" className="font-semibold underline text-grade">
          DU Internal Assessment Grievances &amp; Grace Marks Guidelines 2026
        </a>
        .
      </p>

      {/* ─── Section 5: Societies & Extracurriculars ─── */}
      <h2>5. College Societies &amp; Extracurricular Strategy</h2>
      <p>
        Delhi University societies are renowned for fostering leadership, corporate networking, and creative expression. However, joining too many societies leads to academic burnout and attendance shortfalls.
      </p>

      <h3>5.1 Tiering DU Societies for Strategic CV Building</h3>
      <div className="space-y-4 my-6">
        <div className="border border-rule bg-card p-4">
          <strong className="block text-base mb-1 text-grade font-semibold">Tier 1: High Corporate &amp; Analytical Value</strong>
          <p className="text-sm">
            <strong>Finance &amp; Investment Cell (FIC), Placement Cell (Placement Cell / Placement Cell Core), Consulting Club, Enactus.</strong><br />
            <em>Value:</em> Direct exposure to corporate case competitions, live consulting projects, financial modeling workshops, and placement drive management. Highly valued by consulting firms (MBB) and investment banks during campus recruitment.
          </p>
        </div>
        <div className="border border-rule bg-card p-4">
          <strong className="block text-base mb-1 font-semibold">Tier 2: Public Speaking &amp; Leadership Development</strong>
          <p className="text-sm">
            <strong>Debating Society (DebSoc), Model United Nations (MUN Society), Rotaract Club, Editorial / Publications Board.</strong><br />
            <em>Value:</em> Sharpens parliamentary debate skills, research agility, editorial synthesis, and event management. Excellent preparation for civil services, law, and corporate communications.
          </p>
        </div>
        <div className="border border-rule bg-card p-4">
          <strong className="block text-base mb-1 font-semibold">Tier 3: Performing Arts &amp; Cultural Expression</strong>
          <p className="text-sm">
            <strong>Dramatics Society (Stage &amp; Street Play / Nukkad Natak), Music Society, Dance Society, Fine Arts.</strong><br />
            <em>Value:</em> Builds intense teamwork, performance confidence, and national competition experience across IIT/IIM cultural fests. Demands heavy evening practice hours (often 3–4 hours daily).
          </p>
        </div>
      </div>

      <h3>5.2 The 1-Core + 1-Interest Rule</h3>
      <p>
        Senior mentors advise freshers to restrict active society memberships to maximum <strong>two societies</strong> during Semester 1:
      </p>
      <ul>
        <li><strong>One Career-Oriented Core Society:</strong> (e.g., Placement Cell, Finance Cell, or Consulting Club).</li>
        <li><strong>One Passion-Oriented Creative Society:</strong> (e.g., Debating, Photography, or Music).</li>
      </ul>
      <p>
        This balance preserves sufficient time for academic lectures, tutorials, and self-study while building a well-rounded resume.
      </p>

      {/* ─── Section 6: Internships & Career Roadmap ─── */}
      <h2>6. First-Year Career Roadmap &amp; Skill Development</h2>
      <p>
        A common myth among freshers is that corporate internships are reserved for 3rd-year students. In reality, building a strong resume begins in Semester 1 through targeted micro-internships, live research projects, and technical skill acquisition.
      </p>

      <h3>6.1 Semester-by-Semester Execution Timeline</h3>
      <div className="overflow-x-auto my-6">
        <table className="w-full text-left text-sm border-collapse border border-rule">
          <thead>
            <tr className="bg-card font-semibold border-b border-rule">
              <th className="p-3 border-r border-rule">Academic Phase</th>
              <th className="p-3 border-r border-rule">Primary Focus</th>
              <th className="p-3 border-r border-rule">Key Skill / Certification Target</th>
              <th className="p-3">Actionable Outcome</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-rule">
            <tr>
              <td className="p-3 border-r border-rule font-medium">Semester 1 (Aug – Dec)</td>
              <td className="p-3 border-r border-rule">Academic Stabilization &amp; Core Skills</td>
              <td className="p-3 border-r border-rule">Advanced MS Excel, Financial Modeling, or Python / R basics.</td>
              <td className="p-3">Maintain SGPA &gt; 8.0; secure member position in 1-2 college societies.</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">Winter Break (Dec – Jan)</td>
              <td className="p-3 border-r border-rule">First Internship / NGO Work</td>
              <td className="p-3 border-r border-rule">Content Research, Digital Marketing, or Social Sector Analytics.</td>
              <td className="p-3">Complete 4-week winter internship; update LinkedIn &amp; 1-page CV.</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">Semester 2 (Jan – May)</td>
              <td className="p-3 border-r border-rule">Case Competitions &amp; Research Papers</td>
              <td className="p-3 border-r border-rule">Corporate Case Solving, Data Visualization (Tableau / PowerBI).</td>
              <td className="p-3">Participate in campus case competitions (IIM / DU fest circuit).</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">Summer Break (Jun – Jul)</td>
              <td className="p-3 border-r border-rule">Corporate Summer Internship</td>
              <td className="p-3 border-r border-rule">Research Associate, Business Analytics, or Financial Research.</td>
              <td className="p-3 font-semibold text-grade">Complete 8-week corporate internship with tangible project impact metrics.</td>
            </tr>
          </tbody>
        </table>
      </div>

      <p>
        Curious about career paths after graduation? Compare post-DU options in our analytical guide on{" "}
        <a href="/blog/cat-vs-civil-services-vs-masters" className="font-semibold underline text-grade">
          CAT vs Civil Services vs Master's: Evaluating Post-DU Career Decisions
        </a>{" "}
        and explore empirical outcome data in{" "}
        <a href="/blog/what-economics-honours-grads-do" className="font-semibold underline text-grade">
          What Do Economics Honours Graduates Actually Do?
        </a>
        .
      </p>

      {/* ─── Section 7: Common Mistakes ─── */}
      <h2>7. Top 10 Mistakes First-Year Students Must Avoid</h2>
      <div className="space-y-3 my-6">
        <div className="border border-rule bg-card p-4">
          <strong className="block text-base mb-1 font-semibold text-grade">1. Neglecting Continuous Assessment (CA) Tests</strong>
          <p className="text-sm">Class tests account for 25 out of 100 marks per paper. Skipping tests assuming end-sem theory exams will make up for it mathematically caps your maximum grade at a B+.</p>
        </div>
        <div className="border border-rule bg-card p-4">
          <strong className="block text-base mb-1 font-semibold">2. Falling Below 66.6% Attendance Early</strong>
          <p className="text-sm">Missing lectures in August and September creates an impossible attendance backlog by November. Calculate your required lecture count weekly to stay above 70% safely.</p>
        </div>
        <div className="border border-rule bg-card p-4">
          <strong className="block text-base mb-1 font-semibold">3. Choosing SEC/VAC Papers Based Only on Course Titles</strong>
          <p className="text-sm">Picking "Advanced Data Analytics" without prior coding experience can drag your SGPA down. Choose SEC/VAC papers with high average grade yield and manageable practical components.</p>
        </div>
        <div className="border border-rule bg-card p-4">
          <strong className="block text-base mb-1 font-semibold">4. Buying Outdated Textbooks Before Reading Syllabus Units</strong>
          <p className="text-sm">Wait for your course professor to recommend specific reading lists and standard reference texts. Many library copies and digital PDFs are available for free.</p>
        </div>
        <div className="border border-rule bg-card p-4">
          <strong className="block text-base mb-1 font-semibold">5. Overcommitting to 4+ Societies</strong>
          <p className="text-sm">Spreading yourself thin across multiple societies results in dropped attendance, missed assignment deadlines, and eventual burnout.</p>
        </div>
        <div className="border border-rule bg-card p-4">
          <strong className="block text-base mb-1 font-semibold">6. Ignoring Master's Eligibility Criteria for GE Papers</strong>
          <p className="text-sm">If you plan to apply for MA Economics at DSE, JNU, or IGIDR, failing to take Math GE papers in your undergraduate years will disqualify your application regardless of your entrance test score.</p>
        </div>
        <div className="border border-rule bg-card p-4">
          <strong className="block text-base mb-1 font-semibold">7. Relying Only on Unofficial WhatsApp Groups</strong>
          <p className="text-sm">Always verify exam schedules, assignment submission portals, and notice circulars on official college websites and SAMARTH notices.</p>
        </div>
        <div className="border border-rule bg-card p-4">
          <strong className="block text-base mb-1 font-semibold">8. Mismanaging Exam Answer Writing Time</strong>
          <p className="text-sm">DU end-sem exams demand structured answer writing with definitions, derivations, graphs, and evaluative conclusions. Learn time allocation in our <a href="/blog/du-economics-exam-first-5-minutes" className="underline font-medium text-grade">DU Exam 5-Minute Strategy</a>.</p>
        </div>
        <div className="border border-rule bg-card p-4">
          <strong className="block text-base mb-1 font-semibold">9. Leaving SGPA to CGPA Calculations for Final Year</strong>
          <p className="text-sm">Understand credit weights early. Use our step-by-step guide on <a href="/blog/how-to-calculate-sgpa-to-cgpa-in-du-2026" className="underline font-medium text-grade">How to Calculate SGPA and CGPA in DU</a> to monitor your academic progress semester by semester.</p>
        </div>
        <div className="border border-rule bg-card p-4">
          <strong className="block text-base mb-1 font-semibold">10. Isolating Academic Study from Career Positioning</strong>
          <p className="text-sm">Combine academic excellence with practical skills. Build your LinkedIn profile, participate in case competitions, and maintain consistent dialogue with college placement cells from your first semester.</p>
        </div>
      </div>

      {/* ─── Section 8: Related Resources ─── */}
      <h2>8. Essential First-Year Reading List</h2>
      <p>
        Bookmark these foundational academic and operational guides to stay ahead in your first semester at Delhi University:
      </p>
      <ul>
        <li><a href="/blog/how-to-calculate-sgpa-to-cgpa-in-du-2026" className="font-semibold underline text-grade">How to Calculate SGPA and CGPA in DU (UGCF 2022)</a></li>
        <li><a href="/blog/du-ge-sec-vac-course-selection-2026" className="font-semibold underline text-grade">DU GE, SEC &amp; VAC Course Selection 2026 Guide</a></li>
        <li><a href="/blog/du-grading-attendance-explained" className="font-semibold underline text-grade">DU Grading &amp; 66.6% Attendance Rules Explained</a></li>
        <li><a href="/blog/du-internal-assessment-grace-marks-guidelines-2026" className="font-semibold underline text-grade">DU Internal Assessment &amp; Grace Marks Guidelines 2026</a></li>
        <li><a href="/blog/one-page-cheat-sheets-that-work" className="font-semibold underline text-grade">One-Page Cheat Sheets for College Exams</a></li>
        <li><a href="/blog/best-study-spots-north-campus" className="font-semibold underline text-grade">10 Best Study Spots &amp; Libraries in DU North Campus</a></li>
      </ul>
    </>
  );
}


